import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';

import { Anything } from 'src/types';
import { useSomething } from 'src/queries';
import { createAnything } from 'src/api-services';
import { useNewAnythingHelpers } from 'src/hooks/anything-new';
import AnythingUploadImage from 'src/Components/Anything/AnythingUploadImage';
import { Input, Button } from 'src/UIComponents';

import { Container, FormContainer } from './AnythingQuickAdd.styles';
import { SupportedTypes, NEW_CUSTOM_TYPE } from './SupportedTypes';
import { NewCustomTypeField } from './SupportedTypes/NewCustomTypeField';

type AnythingQuickAddProps = {
  somethingId: number;
};

function AnythingQuickAdd({ somethingId, ...rest }: AnythingQuickAddProps) {
  const value1Ref = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value1, setValue1] = useState('');
  const [value2, setValue2] = useState('');
  const [type, setType] = useState<
    Anything['type'] | typeof NEW_CUSTOM_TYPE | null
  >(null);
  const [newType, setNewType] = useState('');
  const { refetch } = useSomething(somethingId);
  const { getPlaceholder, getValues } = useNewAnythingHelpers();

  const onNewType = useCallback((newType) => {
    setNewType(newType);
  }, []);
  const onChangeValue1 = useCallback((event) => {
    setValue1(event.target.value);
  }, []);
  const onChangeValue2 = useCallback((event) => {
    setValue2(event.target.value);
  }, []);

  const { mutate, isLoading } = useMutation(createAnything, {
    onSuccess: () => {
      setType(null);
      setValue1('');
      setValue2('');
      refetch();
    },
  });

  const placeholders = useMemo(() => {
    if (!type || type === NEW_CUSTOM_TYPE) {
      return ['', ''];
    }
    return getPlaceholder(type);
  }, [getPlaceholder, type]);

  const doSave = useCallback(
    async (type: Anything['type'], autoValues?: boolean) => {
      if (isLoading || type === null) {
        return;
      }

      let first = null;
      let second = null;
      if (autoValues) {
        const values = await getValues(type);
        first = values[0];
        second = values[1];
      } else {
        first = value1;
        second = value2;
      }
      if (!first) {
        console.warn(
          `Unable to store Anything.${String(type)} without any value`
        );
        return;
      }
      mutate({
        type,
        value1: first,
        value2: second,
        SomethingId: somethingId,
      });
    },
    [getValues, isLoading, mutate, somethingId, value1, value2]
  );

  const handleSupportedTypeChange = useCallback(
    (newType: Anything['type'] | typeof NEW_CUSTOM_TYPE) => {
      if (type === newType) {
        setType(null);
        return;
      }

      setType(newType);

      // Save some types as soon as they are clicked.
      if (
        newType !== NEW_CUSTOM_TYPE &&
        ['POSITION', 'DATE', 'DATETIME'].includes(newType)
      ) {
        doSave(newType, true);
        return;
      }
    },
    [doSave, type]
  );

  useEffect(() => {
    setValue1('');
    setValue2('');
    if (value1Ref.current) {
      value1Ref.current.focus();
    } else if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [type]);

  const HIDE_TYPES = ['IMAGE_S3', 'TEXT', 'POSITION', 'DATE', 'DATETIME'];

  return (
    <Container {...rest}>
      <SupportedTypes
        onChange={handleSupportedTypeChange}
        exclude={['POSITION', 'DATE', 'DATETIME']}
      />
      {type &&
        type !== NEW_CUSTOM_TYPE &&
        !HIDE_TYPES.includes(type) &&
        `${type}:`}
      <FormContainer>
        {type === 'IMAGE_S3' && (
          <AnythingUploadImage somethingId={somethingId} onComplete={refetch} />
        )}
        {type === 'TEXT' && (
          <textarea
            ref={textareaRef}
            value={value1}
            rows={3}
            onChange={onChangeValue1}
            placeholder={placeholders[0]}
            style={{ width: '100%', marginBottom: 0 }}
          />
        )}
        {type && !HIDE_TYPES.includes(String(type)) && (
          <>
            {type === NEW_CUSTOM_TYPE && (
              <NewCustomTypeField onSubmit={onNewType} />
            )}
            <Input
              ref={value1Ref}
              value={value1}
              onChange={onChangeValue1}
              placeholder={placeholders[0]}
              block
              style={{
                flexBasis: '20%',
              }}
            />
            <Input
              value={value2}
              onChange={onChangeValue2}
              placeholder={placeholders[1]}
              block
              style={{
                flexBasis: '20%',
              }}
            />
          </>
        )}
      </FormContainer>

      {type !== null &&
        type !== NEW_CUSTOM_TYPE &&
        !['IMAGE_S3', 'POSITION', 'DATE', 'DATETIME'].includes(type) && (
          <Button
            $color="primary"
            onClick={() => doSave(type)}
            disabled={isLoading}
          >
            Add
          </Button>
        )}
      {type === NEW_CUSTOM_TYPE && (
        <Button
          $color="primary"
          onClick={() => doSave(newType)}
          disabled={isLoading}
        >
          Add
        </Button>
      )}
    </Container>
  );
}

export default AnythingQuickAdd;
